import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";
import { MessageSquare, Clock, CheckCircle2, Send } from "lucide-react";

const CATEGORIES = [
  "Delivery", "Product Quality", "Payout / Wallet", "KYC", "Rank / Business Plan", "Login / Account", "Other",
];

/** Support ticket form for logged-in associates.
 *  Backed by the existing /grievance endpoint — auto-fills the user's name / email / phone / ID.
 *  Also lists the user's past tickets from /grievance/mine if available.
 */
export default function Support() {
  const { user } = useAuth();
  const [form, setForm] = useState({ category: CATEGORIES[0], subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    api.get("/grievance/mine").then((r) => setMine(r.data || [])).catch(() => setMine([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.body.trim()) {
      toast.error("Please add a subject and details");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        user_code: user?.user_code,
        name:      user?.name,
        email:     user?.email,
        phone:     user?.phone || "",
        category:  form.category,
        subject:   form.subject.trim(),
        body:      form.body.trim(),
      };
      const r = await api.post("/grievance", payload);
      toast.success(`Ticket ${r.data.ticket_no || ""} submitted. We'll respond within 30 days per DSR 2021.`);
      setForm({ category: CATEGORIES[0], subject: "", body: "" });
      const r2 = await api.get("/grievance/mine").catch(() => ({ data: [] }));
      setMine(r2.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit ticket");
    } finally { setBusy(false); }
  };

  return (
    <DashboardLayout title="Online Support" subtitle="Raise a ticket — our team responds within 30 days">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 bg-white border border-line p-6 lg:p-8 rounded-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 grid place-items-center bg-plum/10 border border-plum/25 rounded-sm">
              <MessageSquare size={20} className="text-plum" />
            </div>
            <div>
              <h3 className="font-serif text-xl text-royal">Raise a new ticket</h3>
              <p className="text-xs text-navy/60 mt-0.5">Signed in as <span className="font-mono text-royal">{user?.user_code}</span> — {user?.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-widest text-navy/60">Complaint Category</label>
              <select
                data-testid="support-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1 w-full border border-line px-3 py-3 text-sm rounded-sm focus:border-gold focus:outline-none bg-white"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-navy/60">Subject</label>
              <input
                data-testid="support-subject" required value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Short summary of your issue"
                className="mt-1 w-full border border-line px-3 py-3 text-sm rounded-sm focus:border-gold focus:outline-none"
              />
            </div>
          </div>
          <div className="mt-5">
            <label className="text-xs uppercase tracking-widest text-navy/60">Description</label>
            <textarea
              data-testid="support-body" required value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Describe the issue with dates, order/ticket IDs, and any screenshots you may share separately."
              rows={6}
              className="mt-1 w-full border border-line px-3 py-3 text-sm rounded-sm focus:border-gold focus:outline-none"
            />
          </div>
          <button
            data-testid="support-submit" disabled={busy}
            className="mt-6 bg-royal text-white px-6 py-3 hover:bg-royal-deep rounded-sm disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Send size={14} /> {busy ? "Submitting…" : "Submit Ticket"}
          </button>
        </form>

        <aside className="bg-white border border-line p-6 rounded-sm h-fit">
          <h4 className="font-serif text-lg text-royal">My Recent Tickets</h4>
          {mine.length === 0 ? (
            <div className="mt-4 text-xs text-navy/50 leading-relaxed">You have no tickets yet. New tickets will appear here with their status.</div>
          ) : (
            <ul className="mt-4 space-y-3">
              {mine.slice(0, 8).map((t, i) => (
                <li key={i} data-testid={`support-past-${i}`} className="pb-3 border-b border-line last:border-0">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest">
                    <span className="font-mono text-royal">{t.ticket_no || `#${String(i+1).padStart(4,'0')}`}</span>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border ${
                      t.status === "resolved" ? "bg-olive/10 text-olive border-olive/20"
                      : "bg-gold/10 text-gold-deep border-gold/20"
                    }`}>
                      {t.status === "resolved" ? <CheckCircle2 size={9} /> : <Clock size={9} />}
                      {t.status || "open"}
                    </span>
                  </div>
                  <div className="text-sm text-royal mt-1 font-medium">{t.subject}</div>
                  <div className="text-[11px] text-navy/50 mt-0.5">{t.category} · {t.created_at?.slice(0,10)}</div>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-6 pt-4 border-t border-line text-[11px] text-navy/60 leading-relaxed">
            Per India&apos;s Consumer Protection (Direct Selling) Rules 2021, we respond to every grievance within 30 days.
          </div>
        </aside>
      </div>
    </DashboardLayout>
  );
}
